import { useState, type FormEvent } from 'react';
import { Alert, Anchor, Badge, Group, Loader, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { IconAlertCircle, IconBox, IconSearch, IconTruckDelivery, IconUsers } from '@tabler/icons-react';
import { useSearch, type SearchResultRow } from './api';

/** Coarse qualitative label for the raw pg_trgm trigram-similarity score (a 0–1
 * float, meaningless to plant/office staff as a bare number — Item 5a). Thresholds
 * are a deliberately rough cut, not a calibrated model: >=0.6 is a near-exact hit
 * (code or name matched almost verbatim), >=0.3 is still a plausible partial match
 * (transposed/missing letters, partial code), anything below that is a last-resort
 * weak hit pg_trgm surfaced but a human should double-check. */
function matchQualityLabel(score: number): { label: string; color: string } {
  if (score >= 0.6) return { label: 'Strong match', color: 'green' };
  if (score >= 0.3) return { label: 'Partial match', color: 'yellow' };
  return { label: 'Weak match', color: 'gray' };
}

function ResultRow({ result, to, search }: { result: SearchResultRow; to: string; search: Record<string, string> }) {
  const quality = matchQualityLabel(result.score);
  return (
    <Paper withBorder radius="md" p="sm">
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={0}>
          <Text fw={600}>
            {result.code} — {result.name}
          </Text>
          <Badge size="xs" color={quality.color} variant="light" w="fit-content">
            {quality.label}
          </Badge>
        </Stack>
        {/* A bare `Link` (not `component={Link}` polymorphism) so TanStack Router's
         * `search` param type is inferred correctly against the target route,
         * matching the pattern established in features/silo-counts/components/
         * SiloReadingsRegister.tsx. */}
        <Link to={to} search={search}>
          <Anchor component="span" size="sm">
            View
          </Anchor>
        </Link>
      </Group>
    </Paper>
  );
}

interface SearchResultsProps {
  initialQuery?: string;
}

/** Global search results (BUILD_SPEC.md §10, §13; plan.md G25) — grouped by
 * entity kind, each result linking into the relevant report screen pre-filtered
 * on that entity (this feature's own screens, since the sibling module screens
 * those entities natively live in — suppliers/customers registers — don't yet
 * support a deep-link query param and are out of this goal's S0 ownership). */
export function SearchResults({ initialQuery }: SearchResultsProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialQuery ?? '');
  const query = useSearch(initialQuery ?? '');
  const results = query.data?.results ?? [];

  const products = results.filter((r) => r.kind === 'PRODUCT');
  const suppliers = results.filter((r) => r.kind === 'SUPPLIER');
  const customers = results.filter((r) => r.kind === 'CUSTOMER');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const q = value.trim();
    if (q) void navigate({ to: '/search', search: { q } });
  }

  return (
    <Stack gap="xl">
      <Stack gap={2}>
        <Title order={2}>Search</Title>
        <Text c="dimmed" size="sm">
          Fuzzy search across products, suppliers and customers.
        </Text>
      </Stack>

      <form onSubmit={handleSubmit}>
        <TextInput
          placeholder="Search products, suppliers, customers…"
          leftSection={<IconSearch size={16} />}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          maw={420}
        />
      </form>

      {!initialQuery && (
        <Text c="dimmed" size="sm">
          Type a search term above (or use the header search box) to get started.
        </Text>
      )}

      {initialQuery && query.isLoading && (
        <Group>
          <Loader size="sm" />
          <Text c="dimmed">Searching for "{initialQuery}"…</Text>
        </Group>
      )}

      {initialQuery && query.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Search failed. Try again.
        </Alert>
      )}

      {initialQuery && query.data && results.length === 0 && (
        <Text c="dimmed">No matches for "{initialQuery}".</Text>
      )}

      {products.length > 0 && (
        <Stack gap="xs">
          <Group gap={6}>
            <IconBox size={18} />
            <Title order={4}>Products ({products.length})</Title>
          </Group>
          {products.map((r) => (
            <ResultRow key={r.id} result={r} to="/reports/stock-on-hand" search={{ productId: r.id }} />
          ))}
        </Stack>
      )}

      {suppliers.length > 0 && (
        <Stack gap="xs">
          <Group gap={6}>
            <IconTruckDelivery size={18} />
            <Title order={4}>Suppliers ({suppliers.length})</Title>
          </Group>
          {suppliers.map((r) => (
            <ResultRow key={r.id} result={r} to="/reports/purchasing" search={{ supplierId: r.id }} />
          ))}
        </Stack>
      )}

      {customers.length > 0 && (
        <Stack gap="xs">
          <Group gap={6}>
            <IconUsers size={18} />
            <Title order={4}>Customers ({customers.length})</Title>
          </Group>
          {customers.map((r) => (
            <ResultRow key={r.id} result={r} to="/reports/sales" search={{ customerId: r.id }} />
          ))}
        </Stack>
      )}

      <Badge variant="light" color="graphite" w="fit-content">
        Results link into the Reports hub's read-only screens, pre-filtered on the matched entity.
      </Badge>
    </Stack>
  );
}
